/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Drawer, Select, Table, Empty } from 'antd-v5';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { getRequest } from '../../../../../core/apiRequests';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import {
  getClientChat,
  resetMessaging,
  setChatReviewLink,
  setClientAssociate,
} from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import '../../../../../components/Routes/Today/today.css';

const getStatusBadgeClass = status => {
  switch (status) {
    case 'PAID_MANUALLY':
    case 'PAID_BY_CC':
    case 'PAID_WITH_APPLE_PAY':
    case 'PAID_WITH_GOOGLE_PAY':
      return 'today-status-badge paid';
    case 'VIEWED':
      return 'today-status-badge viewed';
    case 'UNPAID':
      return 'today-status-badge sent';
    case 'CANCELED':
      return 'today-status-badge canceled';
    case 'REFUNDED':
      return 'today-status-badge refunded';
    case 'PAYMENT_FAILED':
    case 'REFUND_FAILED':
      return 'today-status-badge failed';
    default:
      return 'today-status-badge';
  }
};

const getStatusLabel = status => {
  switch (status) {
    case 'PAID_MANUALLY':
    case 'PAID_BY_CC':
    case 'PAID_WITH_APPLE_PAY':
    case 'PAID_WITH_GOOGLE_PAY':
      return 'PAID';
    case 'UNPAID':
      return 'SENT';
    case 'PAYMENT_FAILED':
      return 'FAILED';
    case 'REFUND_FAILED':
      return 'REFUND FAILED';
    default:
      return status;
  }
};

const getActivityText = status => {
  switch (status) {
    case 'PAID_MANUALLY':
    case 'PAID_BY_CC':
    case 'PAID_WITH_APPLE_PAY':
    case 'PAID_WITH_GOOGLE_PAY':
      return 'Client paid payment request';
    case 'VIEWED':
      return 'Client viewed payment request';
    case 'CANCELED':
      return 'Client canceled payment request';
    case 'REFUNDED':
      return 'Client refunded payment request';
    case 'PAYMENT_FAILED':
      return 'No payment method available';
    case 'REFUND_FAILED':
      return 'Payment request failed to refund';
    case 'UNPAID':
      return 'Payment request sent to client';
    default:
      return '';
  }
};

const AllPayments = ({ auth }) => {
  const history = useHistory();
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedAssociateId, setSelectedAssociateId] = useState(null);

  const { data: associates } = useQuery(
    ['associates-list'],
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      return getRequest(reqObj);
    },
    {
      enabled: !!auth.merchantId && auth.role !== 'ASSOCIATE',
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const { data: payments, isFetching } = useQuery(
    ['allPayments', page, pageSize, selectedAssociateId],
    async () => {
      try {
        const reqObj = {
          params: ['today', auth.merchantId, auth.userId, 'paginatedPaymentList'],
          query: {
            page,
            count: pageSize,
            sortColumn: 'datetime',
            ascending: false,
            ...(auth.role !== 'ASSOCIATE' && selectedAssociateId ? { associateId: selectedAssociateId } : {}),
          },
        };
        const response = await getRequest(reqObj);
        if (response?.results && response?.pageInfo) {
          return {
            data: response.results,
            total: response.pageInfo.rowCount,
          };
        }
        return { data: [], total: 0 };
      } catch (error) {
        console.error('Error fetching payments:', error);
        return { data: [], total: 0 };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: { data: [], total: 0 },
      enabled: !!auth?.merchantId,
    },
  );

  const handleRowClick = record => {
    history.push(`/Payments/details/${record.payment_request_id}`);
  };

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const getReviewLink = async reviewStoreId => {
    if (reviewStoreId) {
      const reqObj = { params: ['chats', reviewStoreId, 'getReviewLink'] };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        setChatReviewLink(linkData[0].review_link);
      }
    }
  };

  const openChatModal = async client => {
    await getClientChat(client);
    await getReviewLink(client.store_id);
    setClientAssociate(client.associateCount);
    getClientPaymentStatus({ client, auth });
    setShowChat(true);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'datetime',
        key: 'datetime',
        width: '12%',
        render: t => (t ? moment(t, 'YYYYMDD').format('MM/DD/YYYY') : null),
      },
      {
        title: 'Amount',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: '10%',
        render: amount => `$${amount}`,
      },
      {
        title: 'Client Name',
        dataIndex: 'name',
        key: 'name',
        width: '15%',
      },
      {
        title: 'Associate',
        dataIndex: 'associate_name',
        key: 'associate_name',
        width: '12%',
      },
      {
        title: 'Client Recent Activity',
        dataIndex: 'status',
        key: 'activity',
        width: '25%',
        render: status => getActivityText(status),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '12%',
        align: 'center',
        render: status => <span className={getStatusBadgeClass(status)}>{getStatusLabel(status)}</span>,
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        width: '10%',
        align: 'center',
        render: (_, record) => (
          <div
            className="align-center"
            onClick={e => {
              e.stopPropagation();
              openChatModal(record);
            }}
          >
            <FontAwesomeIcon
              className="pointer"
              icon={['far', 'comment-alt-lines']}
              color="#818181"
              style={{ fontSize: '19px' }}
            />
          </div>
        ),
      },
    ],
    [auth],
  );

  return (
    <StandardLayoutV2
      title="Payments"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Payments' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      {auth.role !== 'ASSOCIATE' && (
        <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <Select
            style={{ width: 200 }}
            value={selectedAssociateId}
            onChange={value => {
              setSelectedAssociateId(value || null);
              setPage(1);
            }}
            placeholder="All Associates"
            allowClear
            showSearch
            optionFilterProp="label"
            options={(associates || []).map(a => ({
              label: `${a.first_name || ''} ${a.last_name || ''}`.trim(),
              value: a.id,
            }))}
          />
        </div>
      )}
      <Table
        className="today-table"
        columns={columns}
        dataSource={payments?.data || []}
        rowKey={record => record.payment_request_id || record.id}
        loading={isFetching}
        pagination={{
          pageSize,
          current: page,
          total: payments?.total || 0,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        onRow={record => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Payments" />,
        }}
      />
      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllPayments);
